import { inject, injectable } from 'inversify';
import {
    CONTRACTS,
    AuthServiceContract,
    FetchServiceContract,
    FetchMethod,
    SignInType,
    ConfigServiceContract,
    ConfigServices,
} from '@/core/contracts';
import config from '@/config';

/**
 * Service for queries.
 * @export
 * @class FetchService
 * @implements {FetchServiceContract}
 */
@injectable()
export class FetchService implements FetchServiceContract {

    constructor(
        @inject(CONTRACTS.AuthService) private _authService: AuthServiceContract,
        @inject(CONTRACTS.ConfigService) private _configService: ConfigServiceContract
    ) {
        /* empty constructor*/
     }

    public fetch(
        url: string,
        object?: any,
        method?: FetchMethod,
    ): Promise<Response> {
        const headers = new Headers({
            'Content-Type': 'application/json',
            'Cache-Control': 'no-store, no-cache, must-revalidate, post-check=0, pre-check=0',
            'Pragma': 'no-cache',
        });

        if (this._authService && this._authService.isAuthenticated) {
            headers.set('Authorization', `Bearer ${this._authService.accessToken}`);
        }

        if (url.indexOf('http://') < 0 && url.indexOf('https://') < 0 &&
            url.indexOf(this._configService.getConfig().Services?.identityURL || '') < 0) {
            url = this.buildAbsoluteUrl(url);
        }

        return fetch(url, {
            body: object ? JSON.stringify(object) : undefined,
            headers,
            method,
        }).then((response) => {
            if (response.status === 401 || response.status === 403) {
                this._authService!.signIn(SignInType.Default);
            }
            return response;
        });
    }

    public get<T>(url: string): Promise<T> {
        return this.getJsonFromApi<T>(url);
    }

    public post<T>(url: string, object: any): Promise<T> {
        return this.getJsonFromApi<T>(url, object, FetchMethod.POST);
    }

    public delete<T>(url: string, body?: any): Promise<T> {
        return this.getJsonFromApi<T>(url, body, FetchMethod.DELETE);
    }

    public put<T>(url: string, object: any): Promise<T> {
        return this.getJsonFromApi<T>(url, object, FetchMethod.PUT);
    }

    public buildAbsoluteUrl(url: string): string {
        return this._configService.getConfig().Services?.identityURL + url;
    }

    private async getJsonFromApi<T>(url: string, object?: any, method?: FetchMethod): Promise<T> {
        if (url.indexOf('http://') < 0 && url.indexOf('https://') < 0 &&
            url.indexOf(this._configService.getConfig().Services?.identityURL || '') < 0) {
            url = this.buildApiUrl(url);
        }

        const response = await this.fetch(url, object, method);
        if (response.ok) {
            return response.text().then((text) => text ? JSON.parse(text) : null);
        } else {
            throw (new Error(`${response.status} (${response.statusText})`));
        }
    }

    private buildApiUrl(url: string): string {
        return config.fetch.apiPrefix + url;
    }
}
